import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import Loader from "./Loader";
import { generatePublicUrl } from "../urlConfig";
import "../scss/components/reusableMultiSelectDialog.scss";

const normalizeText = (value) =>
  String(value ?? "")
    .toLocaleLowerCase("ru-RU")
    .replace(/\s+/g, " ")
    .trim();

const getProductImage = (item) => {
  const pictures = Array.isArray(item?.productPictures)
    ? item.productPictures
    : [];
  const picture =
    pictures.find((candidate) => candidate?.mainImg) || pictures[0];

  return item?.reducedImage || picture?.img || null;
};

const getItemFields = (item, type) => {
  switch (type) {
    case "category":
      return {
        image: item?.categoryImage || null,
        name: item?.name || "Категория без названия",
        subtitle: item?.slug || "",
        searchText: [item?.name, item?.slug].filter(Boolean).join(" "),
      };

    case "user":
      return {
        image: null,
        name:
          [item?.firstName, item?.lastName].filter(Boolean).join(" ") ||
          item?.email ||
          "Пользователь",
        subtitle: item?.email || "",
        searchText: [
          item?.firstName,
          item?.lastName,
          item?.email,
          item?.phone,
        ]
          .filter(Boolean)
          .join(" "),
      };

    case "office":
      return {
        image: item?.image || null,
        name: item?.name || "Офис без названия",
        subtitle: item?.address || "",
        searchText: [item?.name, item?.address, item?.phone]
          .filter(Boolean)
          .join(" "),
      };

    case "product":
    default: {
      const dimensions = [
        item?.length,
        item?.width,
        item?.height,
      ]
        .filter(
          (value) =>
            value !== undefined && value !== null && value !== ""
        )
        .join(" × ");

      const article = String(item?.article || "").trim();
      const category =
        item?.categoryName ||
        item?.category?.name ||
        item?.categorySlug ||
        "";
      const name =
        item?.fullName || item?.name || "Товар без названия";

      return {
        image: getProductImage(item),
        name,
        article,
        category,
        dimensions,
        price:
          typeof item?.price === "number" && Number.isFinite(item.price)
            ? item.price
            : null,
        subtitle: [article && `Арт. ${article}`, category]
          .filter(Boolean)
          .join(" · "),
        searchText: [
          name,
          item?.name,
          article,
          item?.slug,
          category,
          item?.nameMaterial,
          dimensions,
          item?.description,
        ]
          .filter(Boolean)
          .join(" "),
      };
    }
  }
};

const formatMoney = (value) =>
  new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const ReusableMultiSelectDialog = ({
  handleClickBottomVisible = true,
  type = "product",
  title,
  description,
  data = [],
  selectedValues = [],
  setSelectedValues,
  onSubmit,
  fetchData,
  disabled = false,
  buttonText = "Добавить",
  inputLabelText = "позицию",
  open: controlledOpen,
  onClose,
  hideTrigger = false,
  loading = false,
  selectionMode = "multiple",
  searchPlaceholder = "Поиск…",
  emptyText = "Ничего не найдено",
  pageSize = 24,
}) => {
  const dispatch = useDispatch();
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("name-asc");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const fetchDataRef = useRef(fetchData);
  const wasOpenRef = useRef(false);

  const isControlled = typeof controlledOpen === "boolean";
  const isOpen = isControlled ? controlledOpen : internalOpen;

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSearch("");
      setSelectedOnly(false);
      setPage(1);

      const action = fetchDataRef.current?.();
      if (action) dispatch(action);
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, dispatch]);

  const closeDialog = () => {
    if (!isControlled) setInternalOpen(false);
    onClose?.();
  };

  const openDialog = () => {
    if (!isControlled) setInternalOpen(true);
  };

  const normalizedSelection = useMemo(
    () => (Array.isArray(selectedValues) ? selectedValues : []),
    [selectedValues]
  );

  const selectedSet = useMemo(
    () =>
      new Set(
        normalizedSelection.map((value) => String(value))
      ),
    [normalizedSelection]
  );

  const preparedItems = useMemo(
    () =>
      (Array.isArray(data) ? data : [])
        .filter((item) => item && item._id)
        .map((item, originalIndex) => ({
          item,
          originalIndex,
          fields: getItemFields(item, type),
        })),
    [data, type]
  );

  const filteredItems = useMemo(() => {
    const query = normalizeText(search);

    const result = preparedItems.filter(({ item, fields }) => {
      const selected = selectedSet.has(String(item._id));

      if (selectedOnly && !selected) return false;
      if (!query) return true;

      return normalizeText(fields.searchText).includes(query);
    });

    result.sort((left, right) => {
      const leftName = String(left.fields.name || "");
      const rightName = String(right.fields.name || "");

      switch (sortMode) {
        case "name-desc":
          return rightName.localeCompare(leftName, "ru", {
            numeric: true,
            sensitivity: "base",
          });

        case "price-asc":
          return (
            Number(left.fields.price ?? Number.MAX_SAFE_INTEGER) -
            Number(right.fields.price ?? Number.MAX_SAFE_INTEGER)
          );

        case "price-desc":
          return (
            Number(right.fields.price ?? -1) -
            Number(left.fields.price ?? -1)
          );

        case "catalog":
          return left.originalIndex - right.originalIndex;

        case "name-asc":
        default:
          return leftName.localeCompare(rightName, "ru", {
            numeric: true,
            sensitivity: "base",
          });
      }
    });

    return result;
  }, [
    preparedItems,
    search,
    selectedOnly,
    selectedSet,
    sortMode,
  ]);

  const safePageSize = Math.max(Number(pageSize) || 24, 1);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredItems.length / safePageSize)
  );

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search, sortMode, selectedOnly]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * safePageSize;
    return filteredItems.slice(start, start + safePageSize);
  }, [filteredItems, page, safePageSize]);

  const toggleItem = (itemId) => {
    if (typeof setSelectedValues !== "function") return;

    const value = String(itemId);
    const exists = selectedSet.has(value);

    if (selectionMode === "single") {
      setSelectedValues(exists ? [] : [itemId]);
      return;
    }

    if (exists) {
      setSelectedValues(
        normalizedSelection.filter(
          (selectedId) => String(selectedId) !== value
        )
      );
      return;
    }

    setSelectedValues([...normalizedSelection, itemId]);
  };

  const selectVisible = () => {
    if (
      selectionMode === "single" ||
      typeof setSelectedValues !== "function"
    ) {
      return;
    }

    const next = [...normalizedSelection];
    const known = new Set(next.map((value) => String(value)));

    pageItems.forEach(({ item }) => {
      const id = String(item._id);
      if (!known.has(id)) {
        next.push(item._id);
        known.add(id);
      }
    });

    setSelectedValues(next);
  };

  const clearSelection = () => {
    setSelectedValues?.([]);
  };

  const handleSubmit = async () => {
    const result = await onSubmit?.(normalizedSelection);
    if (result !== false) closeDialog();
  };

  return (
    <div className="catalog-picker-trigger">
      {!hideTrigger && (
        <Button
          variant="contained"
          endIcon={<AddIcon />}
          color="primary"
          onClick={openDialog}
          disabled={disabled}
        >
          {buttonText}
        </Button>
      )}

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        classes={{ paper: "catalog-picker__paper" }}
        aria-labelledby="catalog-picker-title"
      >
        <DialogTitle
          id="catalog-picker-title"
          className="catalog-picker__title"
          disableTypography
        >
          <div>
            <span className="catalog-picker__eyebrow">
              Каталог
            </span>
            <h2>{title || `Выберите ${inputLabelText}`}</h2>
          </div>

          <Tooltip title="Закрыть">
            <IconButton
              onClick={closeDialog}
              className="catalog-picker__close"
              aria-label="Закрыть"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent dividers className="catalog-picker__content">
          {description && (
            <DialogContentText className="catalog-picker__description">
              {description}
            </DialogContentText>
          )}

          <div className="catalog-picker__toolbar">
            <TextField
              variant="outlined"
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="catalog-picker__search"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              variant="outlined"
              size="small"
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              label="Сортировка"
              className="catalog-picker__sort"
            >
              <MenuItem value="name-asc">
                По названию А–Я
              </MenuItem>
              <MenuItem value="name-desc">
                По названию Я–А
              </MenuItem>
              {type === "product" && (
                <MenuItem value="price-asc">
                  Сначала дешевле
                </MenuItem>
              )}
              {type === "product" && (
                <MenuItem value="price-desc">
                  Сначала дороже
                </MenuItem>
              )}
              <MenuItem value="catalog">
                Как в каталоге
              </MenuItem>
            </TextField>

            <FormControlLabel
              className="catalog-picker__selected-only"
              control={
                <Checkbox
                  checked={selectedOnly}
                  onChange={(event) =>
                    setSelectedOnly(event.target.checked)
                  }
                  color="primary"
                />
              }
              label="Только выбранные"
            />
          </div>

          <div className="catalog-picker__summary">
            <div>
              <strong>{filteredItems.length}</strong>
              <span>
                {search ? " найдено" : " в каталоге"}
              </span>
            </div>

            <div className="catalog-picker__selection-actions">
              <Chip
                label={`Выбрано: ${normalizedSelection.length}`}
                color={
                  normalizedSelection.length
                    ? "primary"
                    : "default"
                }
                size="small"
              />

              {selectionMode !== "single" &&
                pageItems.length > 0 && (
                  <button
                    type="button"
                    onClick={selectVisible}
                  >
                    Выбрать на странице
                  </button>
                )}

              {normalizedSelection.length > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                >
                  Очистить
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="catalog-picker__state">
              <Loader />
              <span>Загрузка каталога…</span>
            </div>
          ) : pageItems.length > 0 ? (
            <div className="catalog-picker__grid">
              {pageItems.map(({ item, fields }) => {
                const selected = selectedSet.has(
                  String(item._id)
                );

                return (
                  <button
                    type="button"
                    key={item._id}
                    className={`catalog-picker__card${
                      selected
                        ? " catalog-picker__card--selected"
                        : ""
                    }`}
                    onClick={() => toggleItem(item._id)}
                    aria-pressed={selected}
                  >
                    <span className="catalog-picker__check">
                      {selected ? (
                        <CheckCircleIcon />
                      ) : (
                        <Checkbox
                          checked={false}
                          tabIndex={-1}
                          disableRipple
                        />
                      )}
                    </span>

                    <span className="catalog-picker__media">
                      {fields.image ? (
                        <img
                          src={generatePublicUrl(fields.image)}
                          alt={fields.name}
                          loading="lazy"
                        />
                      ) : (
                        <span className="catalog-picker__no-image">
                          Нет фото
                        </span>
                      )}
                    </span>

                    <span className="catalog-picker__card-body">
                      <strong title={fields.name}>
                        {fields.name}
                      </strong>

                      {fields.subtitle && (
                        <span className="catalog-picker__subtitle">
                          {fields.subtitle}
                        </span>
                      )}

                      {fields.dimensions && (
                        <span className="catalog-picker__dimensions">
                          {fields.dimensions} мм
                        </span>
                      )}

                      {fields.price !== null &&
                        fields.price !== undefined && (
                          <span className="catalog-picker__price">
                            {formatMoney(fields.price)} ₽
                          </span>
                        )}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="catalog-picker__state catalog-picker__state--empty">
              <SearchIcon />
              <strong>{emptyText}</strong>
              <span>
                Измените поисковый запрос или сбросьте фильтр.
              </span>
            </div>
          )}

          {totalPages > 1 && (
            <div className="catalog-picker__pagination">
              <IconButton
                onClick={() =>
                  setPage((current) =>
                    Math.max(current - 1, 1)
                  )
                }
                disabled={page <= 1}
                aria-label="Предыдущая страница"
              >
                <ChevronLeftIcon />
              </IconButton>

              <span>
                Страница <strong>{page}</strong> из{" "}
                <strong>{totalPages}</strong>
              </span>

              <IconButton
                onClick={() =>
                  setPage((current) =>
                    Math.min(current + 1, totalPages)
                  )
                }
                disabled={page >= totalPages}
                aria-label="Следующая страница"
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
          )}
        </DialogContent>

        <DialogActions className="catalog-picker__actions">
          <Button onClick={closeDialog}>
            Отмена
          </Button>

          {handleClickBottomVisible && (
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!normalizedSelection.length}
            >
              {selectionMode === "single"
                ? buttonText || "Выбрать товар"
                : `${buttonText || "Добавить"}${
                    normalizedSelection.length
                      ? ` (${normalizedSelection.length})`
                      : ""
                  }`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReusableMultiSelectDialog;
